/**
 * DefaultStorageProvider - basic storage provider for @engine/ path resolution.
 * Delegates to fetch for downloads; throws for uploads, listing, and builds.
 */

import { ENGINE_PATH_PREFIX, PROJECT_PATH_PREFIX } from '../const.js';

import { AssetPath, AssetPathEncodeState } from './storageProvider.js';

import type { FileListResult, FileUploadOptions, IStorageProvider } from './storageProvider.js';


export class DefaultStorageProvider implements IStorageProvider {
  public async resolvePath(path: AssetPath, expiry?: number): Promise<AssetPath> {
    if (path.isResolved()) {
      return path;
    }

    if (path.asStringPath.startsWith(PROJECT_PATH_PREFIX)) {
      AssetPath.validatePrefix(path.asStringPath, PROJECT_PATH_PREFIX);
      throw new Error(
        `Cannot use resolvePath with ${PROJECT_PATH_PREFIX} using the default storage provider, ` +
        'you must provide your own storage provider that can resolve them.' +
        'Custom storage providers can be provided via one of the `projectContext` functions.'
      );
    }

    if (path.asStringPath.startsWith(ENGINE_PATH_PREFIX)) {
      path.resolvePath(AssetPath.stripPrefix(path.asStringPath, ENGINE_PATH_PREFIX), AssetPathEncodeState.Unknown);
    } else {
      path.resolvePath(path.asStringPath, AssetPathEncodeState.Unknown);
    }

    return path;
  }

  public async downloadFileAsBuffer(path: AssetPath): Promise<ArrayBuffer> {
    path = await this.resolvePath(path);
    const response = await fetch(path.getResolvedPath());
    this.checkResponse(path, response);
    return response.arrayBuffer();
  }

  public async downloadFileAsJson<T>(path: AssetPath): Promise<T> {
    path = await this.resolvePath(path);
    const response = await fetch(path.getResolvedPath());
    this.checkResponse(path, response);
    return response.json();
  }

  public async downloadFileAsText(path: AssetPath): Promise<string> {
    path = await this.resolvePath(path);
    const response = await fetch(path.getResolvedPath());
    this.checkResponse(path, response);
    return response.text();
  }

  public async uploadFile(path: AssetPath, content: Blob | File | string | ArrayBuffer, options?: FileUploadOptions): Promise<{ path: string; name: string; }> {
    throw new Error(
      'DefaultStorageProvider does not support uploading files. ' +
      'You must provide your own storage provider that can handle uploads.'
    );
  }

  public async listFiles(path: AssetPath, recursive?: boolean, includeHiddenFiles?: boolean): Promise<FileListResult> {
    throw new Error(
      'DefaultStorageProvider does not support listing files. ' +
      'You must provide your own storage provider that can handle listing files.'
    );
  }

  public async exists(path: AssetPath): Promise<boolean> {
    try {
      path = await this.resolvePath(path);
      const response = await fetch(path.getResolvedPath(), { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  }

  public async buildCurrentProject(): Promise<boolean> {
    throw new Error(
      'DefaultStorageProvider does not support building projects. ' +
      'You must provide your own storage provider that can handle building projects.'
    );
  }

  protected checkResponse(path: AssetPath, response: Response) {
    if (response.ok) {
      return;
    }

    throw new Error(JSON.stringify({
      message: 'Failed to fetch',
      path,
      status: response.status,
      statusText: response.statusText
    }));
  }
}
